// src/components/view-profile/ProfileActions.js
import React, { useState, useRef, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MoreVertical, Edit3, Share2, Flag, Mail, Phone, MapPin, User, Settings } from "lucide-react";

const ProfileActions = ({ 
  profile, 
  isOwner, 
  onEdit, 
  onShare, 
  onReport,
  signedIn,
  compact = false,
}) => {
  const { t, ready: i18nReady } = useTranslation(["profile", "common"]);
  
  // Safe translation with fallback
  const safeT = useMemo(() => {
    return (key, fallback = key) => {
      if (!i18nReady) return fallback;
      const result = t(key);
      return result === key ? fallback : result;
    };
  }, [t, i18nReady]);

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatLocation = (location) => {
    if (!location || typeof location !== "object") return null;
    const parts = [location.village, location.sector, location.district].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : null;
  };

  const contactInfo = [
    ...(profile?.email ? [{ icon: Mail, value: profile.email, label: "Email" }] : []),
    ...(profile?.phone ? [{ icon: Phone, value: profile.phone, label: "Phone" }] : []),
    ...(profile?.location ? [{ icon: MapPin, value: formatLocation(profile.location), label: "Location" }] : []),
  ];

  const menuItems = [
    ...(isOwner ? [{ 
      labelKey: "profile.actions.edit", 
      labelDefault: "Edit profile",
      icon: Edit3, 
      action: onEdit,
      color: "text-gray-700 dark:text-gray-300"
    }] : []),
    { 
      labelKey: "profile.actions.share", 
      labelDefault: "Share profile",
      icon: Share2, 
      action: onShare,
      color: "text-gray-700 dark:text-gray-300"
    },
    ...(contactInfo.length > 0 ? [{ 
      labelKey: "profile.sections.contactInfo", 
      labelDefault: "Contact information",
      icon: User,
      action: () => {}, // Handled separately
      color: "text-gray-700 dark:text-gray-300"
    }] : []),
    ...(!isOwner && signedIn ? [{ 
      labelKey: "profile.actions.report", 
      labelDefault: "Report user",
      icon: Flag, 
      action: onReport,
      color: "text-red-600 dark:text-red-400"
    }] : [])
  ];

  const ownerLinks = isOwner
    ? [
        { to: "/settings#privacy", label: safeT("profile.edit.sections.privacySettings.title", "Privacy Settings"), Icon: Settings },
      ]
    : [];

  return (
    <div className={`relative flex justify-end ${compact ? "" : "-mt-4"}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-8 h-8 rounded-full bg-green-50 dark:bg-neutral-700 hover:bg-green-100 dark:hover:bg-neutral-600 flex items-center justify-center transition-colors"
      >
        <MoreVertical size={16} className="text-green-700 dark:text-green-400" />
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-64 bg-white dark:bg-neutral-800 rounded-xl shadow-lg border border-gray-200 dark:border-neutral-700 z-50 py-2">
          {/* Contact Info Section */}
          {contactInfo.length > 0 && (
            <div className="px-4 py-3 border-b border-gray-100 dark:border-neutral-700">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                {safeT("profile.sections.contactInfo", "Contact information")}
              </h3>
              <div className="space-y-2">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-center gap-3 text-sm">
                    <info.icon size={14} className="text-green-600 dark:text-green-400 flex-shrink-0" />
                    <span className="text-gray-600 dark:text-gray-300 truncate">{info.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Items */}
          <div className="py-1">
            {menuItems
              .filter(item => item.labelKey !== "profile.sections.contactInfo")
              .map((item, index) => (
              <button
                key={index}
                onClick={() => {
                  item.action();
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-2 text-sm hover:bg-green-50 dark:hover:bg-neutral-700 transition-colors ${item.color}`}
              >
                <item.icon size={16} />
                {safeT(item.labelKey, item.labelDefault)}
              </button>
            ))}

            {ownerLinks.length > 0 && (
              <>
                <div className="my-1 border-t border-gray-100 dark:border-neutral-700" />
                {ownerLinks.map(({ to, label, Icon }) => (
                  <Link
                    key={to}
                    to={to}
                    onClick={() => setIsOpen(false)}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm hover:bg-green-50 dark:hover:bg-neutral-700 transition-colors text-green-700 dark:text-green-400"
                  >
                    <Icon size={16} />
                    {label}
                  </Link>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileActions;